'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useParams, useRouter } from 'next/navigation';
import { ArrowLeft, CalendarClock, CheckCircle2, CreditCard, Minus, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';

import { useLoadingState } from '@/hooks/useLoadingState';
import { getVocabulary } from '@/lib/business-types/registry';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/components/ui/utils';
import { RecurringOrderToggle } from '@/components/customer-surface/RecurringOrderToggle';
import { CustomerAccountShortcut } from '@/components/customer-surface/CustomerSurface';
import { formatMoney } from '@/lib/money';
import { venueBackTarget } from '@/lib/customer/order-context';

type PackageTier = { id: string; name: string; price_per_person: number };

type MenuItem = {
  id: string;
  title: string;
  description?: string | null;
  image?: string | null;
  category_id?: string | null;
  price: number;
  // Per-type catalog (doc 38 Part 1). A per-person package prices by guest count,
  // optionally via a named tier (Silver/Gold).
  pricing_mode?: string | null;
  type_profile?: { min_headcount?: number | null; tiers?: PackageTier[] | null } | null;
};

/**
 * One header for every state of this page — the form, the confirmation and the
 * nothing-to-book case — so a guest never loses the identity of the business
 * they are booking with halfway through.
 */
function BookHeader({
  title,
  subtitle,
  onBack,
  chow = false,
  businessSlug,
  branchSlug,
  experience,
}: {
  title: string;
  subtitle?: string | null;
  onBack: () => void;
  chow?: boolean;
  businessSlug: string | null;
  branchSlug: string | null;
  experience: 'v1' | 'v2';
}) {
  return (
    <header
      className={cn(
        'sticky top-0 z-10 flex h-14 items-center gap-2 border-b border-[var(--cs-border)] bg-[var(--color-bg-base)]/95 px-2 backdrop-blur-sm',
        chow && 'chow-book-header',
      )}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <button
        type="button"
        aria-label="Go back"
        onClick={onBack}
        className="inline-flex size-10 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-[var(--color-bg-sunken)] outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]"
      >
        <ArrowLeft aria-hidden="true" className="size-5" />
      </button>
      <div className="min-w-0 flex-1 px-1 font-[family-name:var(--font-place-control)]">
        <p className="truncate text-sm font-semibold leading-tight">{title}</p>
        {subtitle ? (
          <p className="truncate text-xs leading-tight text-[var(--color-text-secondary)]">{subtitle}</p>
        ) : null}
      </div>
      <CustomerAccountShortcut businessSlug={businessSlug} branchSlug={branchSlug} experience={experience} />
    </header>
  );
}

function isPerPerson(item: MenuItem): boolean {
  return String(item.pricing_mode || 'flat') === 'per_person';
}

function tiersOf(item: MenuItem): PackageTier[] {
  return Array.isArray(item.type_profile?.tiers) ? (item.type_profile?.tiers as PackageTier[]) : [];
}

// The effective per-person price: the chosen tier's price, else the base price.
function perPersonPrice(item: MenuItem, selectedTierId: string | undefined): number {
  const tier = tiersOf(item).find((t) => t.id === selectedTierId);
  return Number((tier ? tier.price_per_person : item.price) || 0);
}

type Slot = {
  id: string;
  startsAt: string;
  endsAt: string;
  remaining: number;
};

type BookedOrder = {
  id: string;
  scheduled_status?: string | null;
  deposit_required_amount?: number | null;
  deposit_paid_amount?: number | null;
  currency?: string | null;
};

// The public booking experience for scheduled pre-orders (docs/expansion-roadmap-2026/
// 02-scheduled-orders-and-deposits.md). Framed for catering/events -- an event date &
// time (a merchant-configured pickup slot), package selection, guest count, and an
// optional deposit to hold the booking -- while staying usable for any pre-order.
// Backend contract is unchanged: headcount + message are composed into `guestNote`,
// so this is a pure front-end upgrade (a first-class headcount column is a follow-up).
export default function BookPageClient({ chow = false, experience = 'v1' }: { chow?: boolean; experience?: 'v1' | 'v2' }) {
  const params = useParams<{ menuId: string }>();
  const menuId = params.menuId;
  const router = useRouter();

  const [menuTitle, setMenuTitle] = useState('');
  const [currency, setCurrency] = useState<string | null>(null);
  // The branch's type + sub-type. This page is the advance-order front door for
  // every type that can enable `scheduled_pre_order`, so its language has to come
  // from the type rather than assume catering (audit
  // docs/qa/type-system-as-built-audit-2026-08-10.md §3).
  const [businessType, setBusinessType] = useState<string | null>(null);
  const [specificType, setSpecificType] = useState<string | null>(null);
  const [branchName, setBranchName] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  // Doc 71 MNRQ-UX-01: the venue identity for "back"/"close" comes from DATA
  // carved off the menus wire (`businessSlug` / `branchSlug`, projected in
  // app/api/public/menus/[menuId]/items/route.ts from the same rows already in
  // scope), never reconstructed from the URL, never routed through Chow.
  const [businessSlug, setBusinessSlug] = useState<string | null>(null);
  const [branchSlug, setBranchSlug] = useState<string | null>(null);
  const [categories, setCategories] = useState<Array<{ id: string; name?: string | null; description?: string | null }>>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [selectedTiers, setSelectedTiers] = useState<Record<string, string>>({});
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [headcount, setHeadcount] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestContact, setGuestContact] = useState('');
  const [message, setMessage] = useState('');
  const { isLoading: loading, setIsLoading: setLoading } = useLoadingState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookedOrder, setBookedOrder] = useState<BookedOrder | null>(null);

  // Synchronous lock: `submitting` state alone can't stop a same-frame
  // double-tap before React re-renders. The signature-keyed idempotency key
  // additionally lets the server collapse a retry-after-timeout into the
  // original booking (and its atomically booked slot) instead of double-booking.
  const submittingRef = useRef(false);
  const bookingAttemptRef = useRef<{ signature: string; idempotencyKey: string } | null>(null);

  useEffect(() => {
    if (!menuId) return;
    Promise.all([
      fetch(`/api/public/menus/${menuId}/items`).then((res) => res.json()),
      fetch(`/api/public/pickup-slots?menuId=${menuId}`).then((res) => res.json()),
    ])
      .then(([itemsData, slotsData]) => {
        if (itemsData.ok) {
          setMenuTitle(itemsData.menu?.title || '');
          setCurrency(itemsData.menu?.currency || null);
          setBusinessType(itemsData.menu?.businessType ?? null);
          setSpecificType(itemsData.menu?.specificType ?? null);
          setBranchName(itemsData.menu?.branchName ?? null);
          setBusinessName(itemsData.menu?.businessName ?? null);
          setBusinessSlug(itemsData.menu?.businessSlug ?? null);
          setBranchSlug(itemsData.menu?.branchSlug ?? null);
          setCategories(itemsData.categories || []);
          setItems(itemsData.items || []);
        }
        if (slotsData.ok) {
          setSlots(slotsData.slots || []);
        }
      })
      .catch(() => toast.error('Could not load this menu.'))
      .finally(() => setLoading(false));
  }, [menuId]);

  // One derivation, used for every noun on the page. Falls back to `standard`
  // while the payload is still loading, so nothing flashes catering wording at a
  // bakery on first paint.
  const vocab = useMemo(
    () => getVocabulary(businessType, specificType),
    [businessType, specificType],
  );
  const bookingNoun = vocab.advanceBooking;
  const bookingCta = vocab.advanceBookingCta;
  const bookingNounTitle = bookingNoun.charAt(0).toUpperCase() + bookingNoun.slice(1);

  /**
   * Whose booking this is. The page used to title itself with the MENU's name,
   * so a caterer's guests saw a page headed "Main Menu" with no sign of the
   * business they were about to hand an event to.
   */
  const placeName = businessName || branchName || null;
  const placeIdentity = [businessName, branchName].filter(Boolean).join(' · ') || placeName;

  // Doc 71 MNRQ-UX-01: "back"/"close" returns to the venue's own in-app menu
  // surface, resolved from DATA carved off the menus wire (businessSlug /
  // branchSlug), never reconstructed from the URL, never routed through Chow.
  // Falls back to the legacy router.back() ONLY while the wire is still
  // loading or errored (venueBackTarget returns null then) — see
  // lib/customer/order-context.ts venueBackTarget contract.
  const backToMenu = useCallback(() => {
    const target = venueBackTarget({ businessSlug, branchSlug, experience });
    if (target) {
      router.push(target);
    } else {
      router.back();
    }
  }, [businessSlug, branchSlug, experience, router]);

  const setQuantity = useCallback((itemId: string, delta: number) => {
    setQuantities((prev) => ({ ...prev, [itemId]: Math.max(0, (prev[itemId] || 0) + delta) }));
  }, []);

  const money = useCallback(
    (value: number) => {
      if (!currency) return `${Number(value || 0).toFixed(2)}`;
      try {
        return formatMoney(value, currency);
      } catch {
        return `${currency}${Number(value || 0).toFixed(2)}`;
      }
    },
    [currency],
  );

  const headcountNum = useMemo(() => {
    const n = parseInt(headcount, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [headcount]);

  const { selectedCount, subtotal } = useMemo(() => {
    let count = 0;
    let sum = 0;
    for (const item of items) {
      const qty = quantities[item.id] || 0;
      if (qty <= 0) continue;
      count += qty;
      // Per-person packages price by guest count, once , mirrors the server's
      // per-person multiplier (doc 38 Part 1), using the chosen tier's price. A
      // headcount of 0 shows 0 until the guest enters one, which the min-headcount
      // gate then requires.
      sum += isPerPerson(item)
        ? perPersonPrice(item, selectedTiers[item.id]) * headcountNum
        : qty * Number(item.price || 0);
    }
    return { selectedCount: count, subtotal: sum };
  }, [items, quantities, headcountNum, selectedTiers]);

  // The cart as the scheduled-order API (and a recurring snapshot) expects it.
  const cartItems = useMemo(
    () =>
      Object.entries(quantities)
        .filter(([, quantity]) => quantity > 0)
        .map(([menuItemId, quantity]) => ({
          menuItemId,
          quantity,
          selectedTierId: selectedTiers[menuItemId] || undefined,
        })),
    [quantities, selectedTiers],
  );

  // Highest min-headcount across selected per-person packages , the booking can't
  // be submitted below it (server enforces the same, this is the friendly gate).
  /**
   * The menu grouped the way the merchant grouped it. It used to render as one
   * flat alphabetical list — 27 dishes from Bacon to Turkey with no headings —
   * which is unreadable at the exact moment a planner is trying to assemble a
   * package.
   */
  const groupedItems = useMemo(() => {
    const byCategory = new Map<string, MenuItem[]>();
    for (const item of items) {
      const key = item.category_id || '__loose__';
      const bucket = byCategory.get(key);
      if (bucket) bucket.push(item);
      else byCategory.set(key, [item]);
    }

    const groups = categories
      .map((category) => ({
        id: category.id,
        name: category.name || 'Menu',
        description: category.description || null,
        items: byCategory.get(category.id) || [],
      }))
      .filter((group) => group.items.length > 0);

    // Items with no category, or one that was deleted, must still be bookable.
    const claimed = new Set(groups.flatMap((g) => g.items.map((i) => i.id)));
    const loose = items.filter((item) => !claimed.has(item.id));
    if (loose.length > 0) {
      groups.push({ id: '__loose__', name: groups.length > 0 ? 'More' : 'Menu', description: null, items: loose });
    }

    return groups;
  }, [items, categories]);

  const requiredMinHeadcount = useMemo(() => {
    let min = 0;
    for (const item of items) {
      if ((quantities[item.id] || 0) > 0 && isPerPerson(item)) {
        min = Math.max(min, Number(item.type_profile?.min_headcount || 0));
      }
    }
    return min;
  }, [items, quantities]);

  const hasPerPersonSelected = useMemo(
    () => items.some((item) => (quantities[item.id] || 0) > 0 && isPerPerson(item)),
    [items, quantities],
  );

  const headcountBelowMin = requiredMinHeadcount > 0 && headcountNum < requiredMinHeadcount;

  // Compose the human-readable note the merchant sees on the booking. Kept within the
  // API's 300-char limit; headcount leads so it's scannable in the dashboard.
  const composedNote = useMemo(() => {
    const parts: string[] = [];
    const guests = parseInt(headcount, 10);
    if (Number.isFinite(guests) && guests > 0) parts.push(`Headcount: ${guests}`);
    if (message.trim()) parts.push(message.trim());
    return parts.join(' , ').slice(0, 300);
  }, [headcount, message]);

  async function handleSubmit() {
    const selectedItems = cartItems;

    if (!selectedSlotId) {
      toast.error(`Choose a date and time for your ${bookingNoun}.`);
      return;
    }
    if (selectedItems.length === 0) {
      toast.error('Choose at least one item for your package.');
      return;
    }
    if (!guestName.trim() || !guestContact.trim()) {
      toast.error('Add your name and a phone or email so we can confirm.');
      return;
    }
    if (hasPerPersonSelected && headcountNum <= 0) {
      toast.error('Enter your guest count for a per-person package.');
      return;
    }
    if (headcountBelowMin) {
      toast.error(`This package needs at least ${requiredMinHeadcount} guests.`);
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const requestBody = {
        menuId,
        pickupSlotId: selectedSlotId,
        items: selectedItems,
        guestName: guestName.trim() || undefined,
        guestContact: guestContact.trim() || undefined,
        guestNote: composedNote || undefined,
        headcount: headcountNum > 0 ? headcountNum : undefined,
      };
      const signature = JSON.stringify(requestBody);
      if (bookingAttemptRef.current?.signature !== signature) {
        bookingAttemptRef.current = { signature, idempotencyKey: crypto.randomUUID() };
      }

      const res = await fetch('/api/public/scheduled-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...requestBody,
          idempotencyKey: bookingAttemptRef.current.idempotencyKey,
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        toast.error(data.error || 'Could not complete this booking.');
        return;
      }
      setBookedOrder(data.order);
      bookingAttemptRef.current = null;
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  }

  if (loading) {
    return (
      <div className={cn('mx-auto max-w-2xl space-y-4 p-4', chow && 'chow-book')}>
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (bookedOrder) {
    const depositDue = Math.max(
      0,
      Number(bookedOrder.deposit_required_amount || 0) - Number(bookedOrder.deposit_paid_amount || 0),
    );
    const requiresDeposit = bookedOrder.scheduled_status === 'deposit_pending' && depositDue > 0;

    return (
      <div className={cn('flex min-h-screen flex-col', chow && 'chow-book')}>
        <BookHeader
          title={placeIdentity || bookingCta}
          subtitle="Booking status"
          onBack={backToMenu}
          chow={chow}
          businessSlug={businessSlug}
          branchSlug={branchSlug}
          experience={experience}
        />
        <div className="flex flex-1 items-center justify-center p-4">
          <Card className={cn('w-full max-w-md text-center', chow && 'chow-book-card')}>
            <CardHeader>
              <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--color-success)]" />
              <CardTitle>{`Your ${bookingNoun} is confirmed`}</CardTitle>
              <CardDescription>
                {requiresDeposit
                  ? 'One last step , a deposit is required to hold your booking.'
                  : "You're all set. We'll be in touch to confirm the details."}
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex-col gap-2">
              {requiresDeposit && (
                <Button className="w-full" asChild>
                  <Link href={`/pay/${bookedOrder.id}${experience === 'v2' ? '?experience=v2' : ''}`}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Pay deposit ({money(depositDue)})
                  </Link>
                </Button>
              )}
              <Button className="w-full" variant={requiresDeposit ? 'outline' : 'default'} asChild>
                <Link href={`/booking-status/${bookedOrder.id}${experience === 'v2' ? '?experience=v2' : ''}`}>View booking status</Link>
              </Button>
            </CardFooter>
            <div className="px-6 pb-6">
              <RecurringOrderToggle
                menuId={menuId}
                items={cartItems}
                guestName={guestName}
                guestContact={guestContact}
                guestNote={composedNote}
                defaultStartsAt={slots.find((slot) => slot.id === selectedSlotId)?.startsAt ?? null}
              />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const canSubmit =
    Boolean(selectedSlotId) &&
    selectedCount > 0 &&
    Boolean(guestName.trim()) &&
    Boolean(guestContact.trim()) &&
    !(hasPerPersonSelected && headcountNum <= 0) &&
    !headcountBelowMin;

  // NOTHING TO BOOK. The page used to render the entire form under the sentence
  // "No dates are open for booking right now" — a date field with no dates, a
  // full package builder, a contact form, and a permanently disabled submit
  // button at the bottom. A planner could fill the whole thing in and only then
  // discover it was never going to send. A dead end must be stated at the top
  // and must not be dressed as a form.
  if (slots.length === 0) {
    return (
      <div data-customer-surface="" className={cn('flex min-h-dvh flex-col bg-[var(--color-bg-base)] font-[family-name:var(--font-place-control)]', chow && 'chow-book')}>
        <BookHeader
          title={placeIdentity || menuTitle || bookingCta}
          subtitle={placeName ? bookingCta : null}
          onBack={backToMenu}
          chow={chow}
          businessSlug={businessSlug}
          branchSlug={branchSlug}
          experience={experience}
        />
        <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-6 py-16 text-center">
          <span className="mb-5 inline-flex size-14 items-center justify-center rounded-full bg-[var(--color-bg-sunken)]">
            <CalendarClock aria-hidden="true" className="size-6 text-[var(--color-text-secondary)]" />
          </span>
          <h1 className="text-xl font-semibold">{`No ${bookingNoun} dates open yet`}</h1>
          <p className="pt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
            {placeName
              ? `${placeName} hasn't opened any dates for booking. The menu is still worth a look — and they can take a booking directly.`
              : `There are no dates open for booking yet. The menu is still worth a look.`}
          </p>
          <Button variant="outline" className="mt-6 w-full max-w-xs" onClick={backToMenu}>
            Back to the menu
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div data-customer-surface="" className={cn('flex min-h-dvh flex-col bg-[var(--color-bg-sunken)]/20 font-[family-name:var(--font-place-control)]', chow && 'chow-book')}>
      <BookHeader
        title={placeIdentity || menuTitle || bookingCta}
        subtitle={placeName ? bookingCta : null}
        onBack={backToMenu}
        chow={chow}
        businessSlug={businessSlug}
        branchSlug={branchSlug}
        experience={experience}
      />

      <div className={cn(
        'mx-auto w-full flex-1 space-y-4 p-4 pb-40',
        experience === 'v2' ? 'max-w-6xl md:grid md:grid-cols-2 md:items-start md:gap-5 md:space-y-0 md:px-6 lg:px-8' : 'max-w-2xl',
      )}>
        {/* Hero */}
        <div className={cn('rounded-[var(--radius-lg)] border border-[var(--cs-border)] bg-gradient-to-br from-primary/10 via-background to-background p-6', experience === 'v2' && 'md:col-span-2')}>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">{bookingCta}</p>
          {/* Leads with the BUSINESS, not the menu. A planner about to hand over
              an event needs to see whose event page this is; "Main Menu" told
              them nothing and looked like a mis-linked page. */}
          <h1 className="mt-1 text-2xl font-semibold tracking-[-0.03em]">
            {placeName || menuTitle || 'Reserve your date'}
          </h1>
          <p className="mt-2 text-sm leading-6 text-[var(--color-text-secondary)]">
            {`Pick your date, choose what you need, and tell us the details. We'll confirm the rest.`}
          </p>
        </div>

        {/* Event date & time */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4" /> {`${bookingNounTitle} date & time`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {slots.length === 0 ? (
              <p className="text-sm text-[var(--color-text-secondary)]">
                No dates are open for booking right now , please check back soon.
              </p>
            ) : (
              <Select value={selectedSlotId} onValueChange={setSelectedSlotId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a date and time" />
                </SelectTrigger>
                <SelectContent>
                  {slots.map((slot) => (
                    <SelectItem key={slot.id} value={slot.id}>
                      {new Date(slot.startsAt).toLocaleString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}{' '}
                      · {slot.remaining} left
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>

        {/* Guest count */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" /> How many guests?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="headcount">Number of guests</Label>
              <Input
                id="headcount"
                type="number"
                inputMode="numeric"
                min={1}
                placeholder="e.g. 40"
                value={headcount}
                onChange={(event) => setHeadcount(event.target.value)}
                className="max-w-[160px]"
              />
              <p className="text-xs text-[var(--color-text-secondary)]">Helps us prepare the right quantities.</p>
            </div>
          </CardContent>
        </Card>

        {/* Package / items */}
        <Card className={experience === 'v2' ? 'md:col-span-2' : undefined}>
          <CardHeader>
            <CardTitle className="text-base">Choose your package</CardTitle>
            <CardDescription>Select the dishes and quantities for your event.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            {items.length === 0 ? (
              <p className="text-sm text-[var(--color-text-secondary)]">No items are available on this menu yet.</p>
            ) : (
              groupedItems.flatMap((group) => [
                // The merchant's own section heading. Without these the list was
                // 27 items alphabetically from Bacon to Turkey, with no way to
                // tell a starter from a dessert.
                <p
                  key={`heading-${group.id}`}
                  className="px-2 pb-1 pt-4 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)] first:pt-0"
                >
                  {group.name}
                </p>,
                ...group.items.map((item) => {
                const qty = quantities[item.id] || 0;
                const perPerson = isPerPerson(item);
                const tiers = tiersOf(item);
                const unit = perPerson ? perPersonPrice(item, selectedTiers[item.id]) : Number(item.price || 0);
                return (
                  <div
                    key={item.id}
                    className={`rounded-[var(--radius)] px-2 py-2.5 transition-colors ${qty > 0 ? 'bg-primary/5' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      {/* A picture of the dish. The booking page was the only
                          customer surface that showed none — a planner choosing
                          a package for forty people was picking from a list of
                          bare words. */}
                      {item.image ? (
                        <span className="relative size-14 shrink-0 overflow-hidden rounded-lg bg-[var(--color-bg-sunken)]">
                          <Image src={item.image} alt="" fill sizes="56px" className="object-cover" loading="lazy" />
                        </span>
                      ) : null}
                      <div className="min-w-0 flex-1 pr-1">
                        <p className="line-clamp-2 text-sm font-medium leading-snug">{item.title}</p>
                        {item.description ? (
                          <p className="line-clamp-1 text-xs text-[var(--color-text-secondary)]">
                            {item.description}
                          </p>
                        ) : null}
                        <p className="text-xs tabular-nums text-[var(--color-text-secondary)]">
                          {/* A ₵0.00 price is not a price. Sides and extras that
                              carry no charge read as free, not as costing zero
                              cedis — the old rendering put "₵0.00" next to seven
                              items on this menu and looked broken. */}
                          {unit > 0 ? money(unit) : 'Included'}{perPerson && unit > 0 ? ' per person' : ''}
                          {perPerson && qty > 0 && headcountNum > 0
                            ? ` · ${money(unit * headcountNum)} for ${headcountNum}`
                            : ''}
                          {perPerson && Number(item.type_profile?.min_headcount || 0) > 0
                            ? ` · min ${item.type_profile?.min_headcount} guests`
                            : ''}
                        </p>
                      </div>
                      {perPerson ? (
                        // A package is included once and priced per head , a toggle,
                        // not a quantity stepper.
                        <Button
                          variant={qty > 0 ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setQuantities((prev) => ({ ...prev, [item.id]: qty > 0 ? 0 : 1 }))}
                        >
                          {qty > 0 ? 'Added' : 'Add'}
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            aria-label={`Remove one ${item.title}`}
                            onClick={() => setQuantity(item.id, -1)}
                            disabled={qty === 0}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-6 text-center text-sm tabular-nums">{qty}</span>
                          <Button
                            size="icon"
                            variant="outline"
                            aria-label={`Add one ${item.title}`}
                            onClick={() => setQuantity(item.id, 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Tier picker , only for an added per-person package that offers tiers. */}
                    {perPerson && qty > 0 && tiers.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {tiers.map((tier) => {
                          const active = (selectedTiers[item.id] || '') === tier.id;
                          return (
                            <button
                              key={tier.id}
                              type="button"
                              onClick={() =>
                                setSelectedTiers((prev) => ({
                                  ...prev,
                                  [item.id]: active ? '' : tier.id,
                                }))
                              }
                              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                                active
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-[var(--cs-border)] bg-[var(--color-bg-base)] hover:bg-[var(--color-bg-sunken)]'
                              }`}
                            >
                              {tier.name} · {money(Number(tier.price_per_person || 0))}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
                }),
              ])
            )}
          </CardContent>
        </Card>

        {/* Contact + message */}
        <Card className={experience === 'v2' ? 'md:col-span-2' : undefined}>
          <CardHeader>
            <CardTitle className="text-base">Your details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="guest-name">Name</Label>
                <Input id="guest-name" value={guestName} onChange={(event) => setGuestName(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest-contact">Phone or email</Label>
                <Input
                  id="guest-contact"
                  value={guestContact}
                  onChange={(event) => setGuestContact(event.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Anything we should know? (optional)</Label>
              <Textarea
                id="message"
                rows={3}
                placeholder="Dietary needs, delivery address, theme…"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sticky summary + submit */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[var(--cs-border)] bg-[var(--color-bg-base)]/95 backdrop-blur-sm">
        <div className={cn('mx-auto flex w-full items-center justify-between gap-4 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]', experience === 'v2' ? 'max-w-6xl sm:px-6 lg:px-8' : 'max-w-2xl')}>
          <div>
            <p className="text-xs text-[var(--color-text-secondary)]">
              {selectedCount > 0 ? `${selectedCount} item${selectedCount === 1 ? '' : 's'}` : 'No items yet'}
            </p>
            <p className="text-lg font-semibold tabular-nums">{money(subtotal)}</p>
          </div>
          <Button
            size="lg"
            className="min-w-[160px]"
            onClick={() => void handleSubmit()}
            loading={submitting}
            disabled={!canSubmit}
          >
            {submitting ? 'Booking…' : 'Request booking'}
          </Button>
        </div>
      </div>
    </div>
  );
}
